import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Form, FormGroup, Label, Input, Button } from 'reactstrap';
import { Breadcrumbs } from '../../../AbstractElements';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useParams, useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../../Config/api';
import authHeader from '../../Customer/authHeader';

const EditUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    username: '',
    firstname: '',
    lastname: '',
    email: '',
    password: '',
    role: 'user'
  });

  const [loading, setLoading] = useState(false);
  const [fetchingUser, setFetchingUser] = useState(true);

  useEffect(() => {
    fetchUser();
  }, [id]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users/${id}`, authHeader());
      const user = response.data;
      setFormData({
        username: user.username || '',
        firstname: user.firstname || '',
        lastname: user.lastname || '',
        email: user.email || '',
        password: '', // Don't populate password
        role: user.role || 'user'
      });
      setFetchingUser(false);
    } catch (error) {
      console.error('Error fetching user:', error);
      toast.error('Failed to fetch user details');
      setFetchingUser(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.username || !formData.email) {
      toast.error('Username and email are required');
      return;
    }

    if (formData.password && formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      // Only send password if it's been filled in
      const updateData = {
        username: formData.username,
        firstname: formData.firstname,
        lastname: formData.lastname,
        email: formData.email,
        role: formData.role
      };

      if (formData.password) {
        updateData.password = formData.password;
      }

      const response = await axios.put(`${API_BASE_URL}/api/users/${id}`, updateData, authHeader());

      if (response.data.message) {
        toast.success(response.data.message);
        navigate('/user-list');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.error || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  if (fetchingUser) {
    return (
      <>
        <Breadcrumbs parent="Users" title="Edit User" mainTitle="Edit User" />
        <Container fluid={true}>
          <Row>
            <Col sm="12">
              <Card>
                <CardBody>
                  <div className="text-center">Loading user details...</div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  return (
    <>
      <Breadcrumbs parent="Users" title="Edit User" mainTitle="Edit User" />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <CardHeader>
                <h5>Edit User</h5>
              </CardHeader>
              <CardBody>
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="username">Username *</Label>
                        <Input
                          type="text"
                          name="username"
                          id="username"
                          placeholder="Enter username"
                          value={formData.username}
                          onChange={handleChange}
                          required
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="email">Email *</Label>
                        <Input
                          type="email"
                          name="email"
                          id="email"
                          placeholder="Enter email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="firstname">First Name</Label>
                        <Input
                          type="text"
                          name="firstname"
                          id="firstname"
                          placeholder="Enter first name"
                          value={formData.firstname}
                          onChange={handleChange}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="lastname">Last Name</Label>
                        <Input
                          type="text"
                          name="lastname"
                          id="lastname"
                          placeholder="Enter last name"
                          value={formData.lastname}
                          onChange={handleChange}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="password">New Password (leave blank to keep current)</Label>
                        <Input
                          type="password"
                          name="password"
                          id="password"
                          placeholder="Enter new password (min 6 characters)"
                          value={formData.password}
                          onChange={handleChange}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="role">Role *</Label>
                        <Input
                          type="select"
                          name="role"
                          id="role"
                          value={formData.role}
                          onChange={handleChange}
                        >
                          <option value="user">User</option>
                          <option value="customer">Customer</option>
                          <option value="manager">Manager</option>
                          <option value="admin">Admin</option>
                          <option value="superadmin">Super Admin</option>
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Button color="primary" type="submit" disabled={loading}>
                        {loading ? 'Updating User...' : 'Update User'}
                      </Button>
                      {' '}
                      <Button color="secondary" type="button" onClick={() => navigate('/user-list')}>
                        Cancel
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default EditUser;
